import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  gridClasses,
  GridColDef,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SplitPane, Pane } from 'react-split-pane';
import { useColumnContextAPI } from '../../context/GetAllColumnContext';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import { customValidator, postRequest, usePinnedActionColumn } from '../../utils';
import Spinners from '../Spinner';
import { EditToolbar } from './GridHeader';

import { ProgressBar } from '@airbus/components-react';
import { nanoid } from 'nanoid';
import { createUseStyles } from 'react-jss';
import { t } from '../../../i18next';
import { useAlertBar } from '../../context/AlertBarContext';
import GridDataContextProvider, { useGridDataContext } from '../../context/GridDataContext';
import { useUserContextApi } from '../../context/UserContext';
import { handleClipboardCopy } from '../../utils/clipboardUtils';
import useGridColumnAccess from '../../utils/useGridColumnAccess';
import { withErrorBoundary } from '../layout/ErrorBoundary';
import RightPanel from '../RightPanelForm';
import CustomGridFilterPanel from './CustomGridFilterPanel';
import {
  defaultSorting,
  getApplyQuickFilterFnExceptStatusCols,
  getRowClassName,
} from './GridHelper';
import { PaginationComponent } from './PaginationComponent';
import { RowErrorAction } from './RowErrorAction';
import SetColumnConfig, { getHeaderName } from './SetColumnConfig';
import { withAirbusTooltip } from './withAirbusTooltip';

export default withErrorBoundary(GridTableWithContext);

function GridTableWithContext(props) {
  return (
    <GridDataContextProvider>
      <GridTable {...props} />
    </GridDataContextProvider>
  );
}

function GridTable(props) {
  const classes = useStyles();
  const { tableRes } = props;
  const { mbdDataGrid } = tableRes || {};
  const { userDetails } = useUserContextApi() || {};
  const { language } = useLanguageContextAPI() || {};
  const { roleIds, screenID } = userDetails || {};

  const { setAlertbar, messages } = useAlertBar() || {};

  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useGridDataContext();
  const [rowModesModel, setRowModesModel] = useState({});
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [rowClicked, setRowClicked] = useState(null);
  const [isFormPanelOpen, setIsFormPanelOpen] = useState(!mbdDataGrid?.panelCancelBtn);
  const [isRowLoading, setIsRowLoading] = useState(true);
  const [actionProp] = useState(null);
  const [accordionDetails, setAccordionDetails] = useState(null);
  const [restoreState, setRestoreState] = useState(defaultSorting);
  const [fetchRowQuery, setFetchRowQuery] = useState(null);
  const {
    reRender,
    allColumns,
    allAccessibleColumns,
    viewData,
    selectedViewName,
    setStateAllAccessibleColumnData,
    setSelectedViewName,
    selectedView,
    setSelectedView,
    setInitialViewColumns,
    allDropdowns,
    setStateAllRowsData,
    accessDetails: access,
    tableFeatureData,
    customConfig,
    updateRightTabMenuDetails,
    isDirectConfigScreen,
  } = useColumnContextAPI();
  const ColumnConfig = customConfig?.SetColumnConfig ?? SetColumnConfig;
  const AdditionalFilterQuery = customConfig?.AdditionalFilterQuery ?? '';
  const AdditionalSortQuery = customConfig?.AdditionalSortQuery ?? '';
  const ScreenSlotProps = customConfig?.ScreenSlotProps ?? {};
  const { isColumnConfigLoading, isError } = useGridColumnAccess({
    tableRes,
    roleIds,
    screenID,
    useColumnContextAPI,
  });
  if (isError) {
    setRows(() => []);
    setColumns(() => []);
    setAccordionDetails(null);
    setAlertbar([
      {
        id: nanoid(),
        children: t('data_not_available'),
        severity: 'warning',
        autoHide: true,
      },
    ]);
  }
  const apiRef = useGridApiRef();
  useEffect(() => {
    if (customConfig?.ScreenSlotProps?.customColumns?.length) {
      setStateAllAccessibleColumnData(() => customConfig?.ScreenSlotProps?.customColumns);
      setLocalColumns(customConfig?.ScreenSlotProps?.customColumns);
    }
    if (customConfig?.ScreenSlotProps?.customRows) {
      setRows(customConfig?.ScreenSlotProps?.customRows ?? null);
      setStateAllRowsData(customConfig?.ScreenSlotProps?.customRows ?? null);
      setIsFormPanelOpen(false);
      setIsRowLoading(false);
      setAlertbar([]);
    }
    if (customConfig?.ScreenSlotProps?.customSorting) {
      defaultSorting.sorting = customConfig?.ScreenSlotProps?.customSorting;
    }
    setRestoreState(defaultSorting);
  }, [customConfig]);

  const refreshPinnedColumns = () => {
    setTimeout(() => {
      if (apiRef.current) {
        const currentPinned = apiRef.current.state.pinnedColumns;
        apiRef.current.setPinnedColumns(currentPinned);
        apiRef.current.resize();
      }
    }, 0);
  };

  useEffect(() => {
    if (allColumns && access && allDropdowns && !isDirectConfigScreen) {
      const { gridColumns } = ColumnConfig({
        allColumns,
        access,
        allDropdowns,
        language,
        viewData,
        customProps: {
          updateRow,
          setAlertbar,
          tableFeatureData,
          language,
          ...ScreenSlotProps,
          setRows,
          screenGridId: tableRes.id,
        },
      });
      const queryForGridColumnsList = gridColumns.reduce((resp, item) => {
        if (item.attrAvailableInGrid) {
          resp += item.attrInternalName + ' ';
        }
        return resp;
      }, '');
      const tableSTName = `${mbdDataGrid.schemaName}_${mbdDataGrid.dataGridSource}`;
      const operationsRowDoc = `
      query MyQuery {
        ${tableSTName} ${
          AdditionalSortQuery ||
          `${
            mbdDataGrid.dataGridUniqueIdentifier
              ? `(order_by: {${mbdDataGrid.dataGridUniqueIdentifier}: desc}${AdditionalFilterQuery})`
              : `${AdditionalFilterQuery}`
          }`
        }  {${queryForGridColumnsList}}
      }
    `;
      setStateAllAccessibleColumnData(gridColumns);
      setFetchRowQuery(operationsRowDoc);
    }
  }, [
    allColumns,
    access,
    reRender,
    allDropdowns,
    AdditionalFilterQuery,
    AdditionalSortQuery,
    language,
  ]);

  useEffect(() => {
    if (fetchRowQuery) {
      setIsFormPanelOpen(false);
      setLocalRows(fetchRowQuery);
    }
  }, [fetchRowQuery, reRender]);

  useEffect(() => {
    if (viewData && allAccessibleColumns?.length) {
      const hasStandardView = viewData.find((x) => x.standardView);
      if (!hasStandardView) {
        setAlertbar([
          {
            id: nanoid(),
            children: t('standard_view_not_available'),
            severity: 'warning',
            autoHide: true,
          },
        ]);

        return;
      }
      const defaultView = viewData.find((x) => x.defaultView);
      let selected = null;
      if (selectedViewName) {
        selected = viewData.find((x) => x.viewName === selectedViewName);
      } else if (defaultView) {
        selected = defaultView;
      } else if (hasStandardView) {
        selected = hasStandardView;
      }
      const initialColumns = getOrderedColumns({ selectedView: selected, allAccessibleColumns });
      const columnsList =
        initialColumns
          ?.filter((item) => item.attrAvailableInGrid)
          .map((item) => item.attrInternalName) ?? [];
      setSelectedViewName(selected?.viewName);
      setSelectedView(selected);
      setInitialViewColumns(columnsList);
    }
  }, [selectedViewName, viewData, allAccessibleColumns]);

  useEffect(() => {
    if (selectedView) {
      const viewColumns = getOrderedColumns({ selectedView, allAccessibleColumns });
      setLocalColumns(viewColumns);
      if (AdditionalSortQuery) {
        setRows(
          rows.map((obj, index) => {
            return { ...obj, order: index + 1 };
          }),
        );
        defaultSorting.sorting = {
          sortModel: [
            {
              field: 'order',
              sort: 'desc',
            },
          ],
        };
      }
      let newState = defaultSorting;
      const defaultPagination = {
        paginationModel: {
          page: 0,
          pageSize: access?.noOfRowsPerPage,
        },
      };
      newState = { ...newState, pagination: defaultPagination };
      if (selectedView?.gridFilter) {
        newState = selectedView?.gridFilter;
      } else if (customConfig?.ScreenSlotProps?.customSorting) {
        newState.sorting = customConfig?.ScreenSlotProps?.customSorting;
      }
      setRestoreState(newState);
    }
  }, [customConfig, selectedView, language, isFormPanelOpen, accordionDetails]);

  const hasMountedRef = useRef(false);
  const prevRowIdsRef = useRef(new Set());
  const prevAccordionRef = useRef([]);
  const pendingRowFlagRef = useRef(false);
  const newRowIdRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(rows)) return;

    const currentIds = new Set(rows.map((r) => r?.id));

    if (!hasMountedRef.current) {
      prevRowIdsRef.current = currentIds;
      hasMountedRef.current = true;
      return;
    }
    const newRow = rows.find((r) => r?.status === 'N' && !prevRowIdsRef.current.has(r?.id));

    if (newRow?.id != null) {
      newRowIdRef.current = newRow.id;
      pendingRowFlagRef.current = true;
    }
    prevRowIdsRef.current = currentIds;
  }, [rows]);

  useEffect(() => {
    if (!Array.isArray(accordionDetails)) return;

    const accordionChanged =
      JSON.stringify(prevAccordionRef.current) !== JSON.stringify(accordionDetails);

    const anyObjectChanged = accordionDetails.some((item, index) => {
      const prevItem = prevAccordionRef.current[index];
      return JSON.stringify(item) !== JSON.stringify(prevItem);
    });

    const isValidAccordion = accordionDetails.length > 0;
    const hasNewRow = pendingRowFlagRef.current && newRowIdRef.current != null;

    const rowInAccordion = hasNewRow
      ? accordionDetails.some((item) => item?.id === newRowIdRef.current)
      : false;

    const shouldTriggerAlert =
      (hasNewRow && accordionChanged && isValidAccordion && rowInAccordion) || anyObjectChanged;

    if (shouldTriggerAlert) {
      setAlertbar({
        title: t('rowErrorValidation.title'),
        children: t('rowErrorValidation.children'),
        severity: 'error',
      });

      if (hasNewRow) {
        pendingRowFlagRef.current = false;
        newRowIdRef.current = null;
      }
    }
    prevAccordionRef.current = accordionDetails;
  }, [accordionDetails]);

  const setLocalRows = (operationsRowDoc) => {
    if (typeof operationsRowDoc == 'undefined') {
      return;
    }
    setIsRowLoading(true);
    setAccordionDetails(null);
    const body = {
      columns: allAccessibleColumns
        .filter((col) => col.attrAvailableInGrid)
        .map((col) => ({ field: col.field, type: col.type })),
      screenGridId: tableRes.id,
      customFilter: AdditionalFilterQuery,
    };
    postRequest(`/api/dataGrid`, body).then((res) => {
      if (!res || res?.errors) {
        setIsRowLoading(false);
        setAlertbar([
          {
            id: nanoid(),
            children: t('error.error_fetching'),
            severity: 'error',
            autoHide: true,
          },
        ]);
        return;
      }
      const tableSTName = `${mbdDataGrid.schemaName}_${mbdDataGrid.dataGridSource}`;
      const { SetRowLogic } = customConfig;
      const rowData = SetRowLogic ? SetRowLogic(res[tableSTName]) : res[tableSTName];
      const dArray = rowData.map((result, index) => {
        const data = { status: '', backup: { ...result }, ...result };
        if (mbdDataGrid.dataGridUniqueIdentifier) {
          data.id = result[mbdDataGrid.dataGridUniqueIdentifier];
        } else {
          data.id = index + 1;
        }
        return data;
      });
      setRows([...dArray]);
      setStateAllRowsData([...dArray]);
      setIsFormPanelOpen(false);
      setIsRowLoading(false);
    });
  };

  const setLocalColumns = (colData) => {
    if (!colData.length) {
      setAlertbar([
        {
          id: nanoid(),
          children: t('no_columns_found'),
          severity: 'warning',
          autoHide: true,
        },
      ]);
    } else if (messages && messages[0]?.children === t('no_columns_found')) {
      setAlertbar([]);
    }
    const defaultColumns = [
      {
        attrInternalName: 'undoDelete',
        field: 'undoDelete',
        headerName: '',
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        disableExport: true,
        attrDisplayNameen: '',
        attrDisplayNamede: '',
        getApplyQuickFilterFn: getApplyQuickFilterFnExceptStatusCols,
        renderCell: (params) => {
          const action = [];
          const { status } = params.row;
          const hasAnyError =
            (accordionDetails?.length ?? 0) > 0 || rows?.some((r) => r?.isCommitRow);
          const exists = new Map(accordionDetails?.map((item) => [item.id, item])).has(params?.id);
          if (params.row?.isCommitRow) {
            const { isValid, errors } = customValidator({
              allColumnDefs: allAccessibleColumns,
              row: params.row,
              SetISCellRequired: customConfig?.SetISCellRequired,
              language,
            });
            if (!isValid && Object.keys(errors).length) {
              action.push(
                <GridActionsCellItem
                  icon={<WarningAmberIcon sx={{ color: '#ff0000 !important' }} />}
                  label='Error'
                />,
              );
            }
          }
          if (exists) {
            const matchedItem = (new Map(accordionDetails.map((item) => [item.id, item])).get(
              params?.id ?? '',
            ) as {
              rowno: string;
              column: Record<string, { field: string; msg: string }>;
            }) ?? { rowno: '', column: {} };
            action.push(
              <RowErrorAction
                params={params}
                matchedItem={matchedItem}
                apiRef={apiRef}
                columns={columns}
              />,
            );
          }
          return (
            <div className={classes.actionRow}>
              <div
                className={classes.errorSlot}
                style={{
                  minWidth: hasAnyError ? 30 : 0,
                  marginRight: hasAnyError ? -5 : 0,
                }}
              >
                {action}
              </div>

              <div className={classes.undoSlot}>
                {(status === 'D' || status === 'E' || status === 'N' || status === 'C') && (
                  <GridActionsCellItem
                    icon={<RestoreIcon sx={{ color: 'secondary' }} />}
                    label='Restore'
                    onClick={() => gridActionRevertHandler(params)}
                  />
                )}
              </div>
            </div>
          );
        },
      },
    ];
    const gridColumns = colData.filter((item) => item.attrAvailableInGrid);
    setColumns(withAirbusTooltip([...defaultColumns, ...gridColumns]));
  };

  const handleSelectionAfterDelete = (newState, id) => {
    if (isFormPanelOpen) {
      const lastRecord = newState.find((item) => item.id === rowSelectionModel[0]);
      setRowSelectionModel([lastRecord?.id ?? newState[0]?.id]);
      setRowClicked(lastRecord ?? newState[0]);
    } else {
      setRowSelectionModel((prev) => prev?.filter((selectedId) => selectedId !== id));
    }
  };

  const removeRowById = (state, id) => {
    const newState = [...state];
    const index = newState.findIndex((item) => item.id === id);
    newState.splice(index, 1);
    return newState;
  };

  const replaceRowById = (state, id, newRow) => {
    const newState = [...state];
    const index = newState.findIndex((item) => item.id === id);
    newState.splice(index, 1, newRow);
    return newState;
  };

  const createInitialRow = (backup) => ({
    id: backup[mbdDataGrid.dataGridUniqueIdentifier],
    status: '',
    backup: { ...backup },
    ...backup,
  });

  const gridActionRevertHandler = (params) => {
    const { id, row } = params;
    const { backup, status } = row;

    const isNewOrCloned = status === 'N' || status === 'C';

    if (isNewOrCloned) {
      setTimeout(() => {
        setRows((prev) => {
          const newState = removeRowById(prev, id);
          handleSelectionAfterDelete(newState, id);
          return newState;
        });
        refreshPinnedColumns();
      }, 200);
    } else {
      const initialRow = createInitialRow(backup);

      setTimeout(() => {
        setRowClicked(initialRow);
      }, 500);

      if (customConfig?.ScreenSlotProps?.setCustomRows) {
        customConfig.ScreenSlotProps.setCustomRows((prev) => replaceRowById(prev, id, initialRow));
      }

      setRows((prev) => replaceRowById(prev, id, initialRow));

      refreshPinnedColumns();
    }

    setAccordionDetails((prev) => prev?.filter((detail) => detail?.id !== id));
  };

  const processRowUpdate = (newRow, oldRow) => {
    if (JSON.stringify(oldRow) !== JSON.stringify(newRow)) {
      const updatedRow = {
        ...newRow,
        status: newRow?.status || 'E',
      };
      if (isFormPanelOpen && rowClicked?.id === updatedRow.id) {
        setRowClicked(() => updatedRow);
      }
      if (customConfig?.ScreenSlotProps?.setCustomRows) {
        customConfig?.ScreenSlotProps?.setCustomRows((preState) =>
          preState.map((row) => (row.id === newRow.id ? updatedRow : row)),
        );
      }
      return updatedRow;
    } else {
      return oldRow;
    }
  };

  const onRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const updateRow = ({ rowId, updatedFields }) => {
    setRows((prestate) => {
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id == rowId);
      const row = newState.find((item) => item.id === rowId);

      const newRow = { ...row, ...updatedFields };
      newState.splice(index, 1, newRow);
      return newState;
    });
  };

  const columnVisibilityModel = {
    errorIcon: Boolean(accordionDetails),
  };

  if (mbdDataGrid.dataGridUniqueIdentifier) {
    columnVisibilityModel[mbdDataGrid.dataGridUniqueIdentifier] = false;
  }

  const slotPropsToolbar = {
    setrows: setRows,
    rows,
    setrowmodesmodel: setRowModesModel,
    rowselectionmodel: rowSelectionModel,
    setRowSelectionModel,
    setrowclicked: setRowClicked,
    isformpanelopen: isFormPanelOpen,
    setAlertbar,
    setAccordionDetails,
    columns,
    selectedViewName,
    setColumns,
    isRowLoading,
  };

  const resetAllCellsToView = (model) => {
    return Object.keys(model).reduce((acc, id) => {
      acc[id] = Object.keys(model[id]).reduce((fieldAcc, field) => {
        fieldAcc[field] = { mode: GridCellModes.View };
        return fieldAcc;
      }, {});
      return acc;
    }, {});
  };

  const resetRowToView = (rowModel = {}) => {
    return Object.keys(rowModel).reduce((acc, field) => {
      acc[field] = { mode: GridCellModes.View };
      return acc;
    }, {});
  };

  const handleCellClick = React.useCallback((params: GridCellParams, event: React.MouseEvent) => {
    if (!params?.isEditable) return;

    if (
      (event.target as any).nodeType === 1 &&
      !event.currentTarget.contains(event.target as Element)
    ) {
      return;
    }

    setCellModesModel((prevModel) => {
      const clearedModel = resetAllCellsToView(prevModel);

      return {
        ...clearedModel,
        [params.id]: {
          ...resetRowToView(prevModel[params.id]),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const onCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  const onCellClick = (params, event) => {
    handleCellClick(params, event);
    if (isFormPanelOpen) {
      setRowClicked({ ...params.row });
    }
  };

  const onCellDoubleClick = (params, event) => {
    const colType = params?.colDef?.type || params?.colDef?.attrDataType;
    if (colType === 'date') {
      event.defaultMuiPrevented = true;
      return;
    }
    if (!event.ctrlKey) {
      event.defaultMuiPrevented = true;
    }
    setRowClicked(params?.row);
    setIsFormPanelOpen(true);
    updateRightTabMenuDetails(0);
  };

  const gridProps = {
    columnVisibilityModel,
    rowModesModel,
    rowSelectionModel,
    getRowClassName: (params) => getRowClassName(params, accordionDetails),
    onRowModesModelChange,
    processRowUpdate,
    cellModesModel,
    onCellModesModelChange,
    onCellClick,
    onCellDoubleClick,
    pagination: !AdditionalSortQuery,
    sorting: true,
    ignoreDiacritics: true,
    autoHeight: false,
    editMode: 'cell',
    rowHeight: 40,
    loading: isRowLoading,
    disableColumnSelector: true,
    disableRowGrouping: true,
    pageSizeOptions: !!access?.noOfRowsPerPage && [10, 20, 50, 100],
    slots: {
      toolbar: EditToolbar,
      pagination: PaginationComponent,
      loadingOverlay: LinearProgress,
      filterPanel: CustomGridFilterPanel,
    },
    slotProps: {
      toolbar: slotPropsToolbar,
    },
    isCellEditable: (params) => {
      if (customConfig?.SetIsCellEditable) {
        const { field, row } = params;
        const { SetIsCellEditable } = customConfig;
        return SetIsCellEditable({ field, row, access, allAccessibleColumns, language });
      }
      return params.row.status !== 'D' && access?.edit_row;
    },
    onProcessRowUpdateError: (error) => {
      return error;
    },
    onRowSelectionModelChange: (rowSelectionModel) => {
      setRowSelectionModel(rowSelectionModel);
      if (rowSelectionModel.length) {
        const [lastElementId] = rowSelectionModel.slice(-1);
        const selectedLastRow = rows.find((item) => item.id === lastElementId);
        setRowClicked(selectedLastRow);
      }
      if (customConfig?.GetSelectedRowData) {
        const selectedRowData = rows.filter((item) => rowSelectionModel.includes(item.id));
        const { GetSelectedRowData } = customConfig;
        GetSelectedRowData(selectedRowData);
      }
    },
    ...customConfig?.ScreenSlotProps?.customGridProps,
  };

  return (
    <div className={classes.gridContainer}>
      {(!viewData && !isDirectConfigScreen) || isColumnConfigLoading ? <Spinners /> : <></>}
      {access && !!columns.length && (
        <div className={classes.gridBox}>
          <SplitPane direction='horizontal'>
            <Pane>
              <DataGridContainer
                gridProps={gridProps}
                restoreState={restoreState}
                columns={columns}
                language={language}
                rows={rows}
                actionProp={actionProp}
                apiRef={apiRef}
                initialGridFilterModel={customConfig?.InitialGridFilterModel}
              />
            </Pane>
            <Pane minSize='300px' size={isFormPanelOpen ? '510px' : '1px'}>
              <div className='h-100'>
                {!!rows.length && access?.singleDatasetGroup && isFormPanelOpen && (
                  <RightPanel
                    setisformpanelopen={setIsFormPanelOpen}
                    rowValues={rowClicked}
                    setrows={setRows}
                    rows={rows}
                    setAlertbar={setAlertbar}
                  />
                )}
              </div>
            </Pane>
          </SplitPane>
        </div>
      )}
      <br />
    </div>
  );
}

const getOrderedColumns = (props) => {
  const { selectedView, allAccessibleColumns } = props;
  return selectedView?.viewColumns.reduce((resp, item) => {
    const columnConfig = allAccessibleColumns?.find((res) => res.attrInternalName === item);
    if (columnConfig) {
      resp.push(columnConfig);
    }
    return resp;
  }, []);
};

function LinearProgress() {
  return <ProgressBar indeterminate />;
}

function DataGridContainer(props) {
  const {
    gridProps,
    restoreState,
    rows,
    actionProp,
    language,
    columns,
    apiRef,
    initialGridFilterModel,
  } = props;

  gridProps.slotProps.toolbar.apiRef = apiRef;
  gridProps.onCellEditStart = onCellEditStart;

  const [newColumns, setNewColumns] = useState([]);
  const [newRows, setNewRows] = useState([]);

  usePinnedActionColumn({
    rows,
    apiRef,
    setNewRows,
  });

  useEffect(() => {
    const newData = columns?.reduce((resp, item) => {
      const { headerName, label } = getHeaderName(item, item.attrMandatory, language);
      resp.push({ ...item, headerName, label });
      return resp;
    }, []);
    setNewColumns(newData);
  }, [columns, language]);

  useEffect(() => {
    if (apiRef?.current && restoreState) {
      // Apply initial grid filter into the restored state
      const appliedFilterModel = initialGridFilterModel || { items: [], logicOperator: 'and' };

      const enhancedState = {
        ...restoreState,
        filter: {
          ...(restoreState?.filter || {}),
          filterModel: appliedFilterModel,
        },
      };

      apiRef.current.restoreState(enhancedState);
      setNewRows(() => [...rows]);
    }
  }, [restoreState, rows]);

  useEffect(() => {
    if (actionProp) {
      const { id, field, rowIndex } = actionProp;
      const state = apiRef.current.state;
      const pagesize = state.pagination.paginationModel.pageSize;
      let rowIndexVal;
      state.sorting.sortedRows.forEach((value, index) => {
        if (value === id) {
          rowIndexVal = index;
        }
      });
      const goto = Math.ceil(
        state.sorting.sortModel.length ? rowIndexVal / pagesize : rowIndex / pagesize,
      );
      apiRef.current.setPage(goto - 1);
      apiRef.current.setCellFocus(id, field);
      const colindex = apiRef.current.getColumnIndex(field, false);
      setTimeout(() => {
        const row = document.querySelectorAll(
          `.MuiDataGrid-virtualScroller .MuiDataGrid-virtualScrollerRenderZone [data-id='` +
            id +
            `'] [data-field=` +
            field +
            `]`,
        );
        if (row?.length) {
          row[0].scrollIntoView(true);
          if (apiRef.current) {
            apiRef.current.scrollToIndexes({ rowIndex: rowIndex, colIndex: colindex - 1 });
          }
        }
      }, 10);
    }
  }, [actionProp]);

  useEffect(() => {
    const resizeGrid = () => {
      apiRef.current?.autosizeColumns();
    };
    window.addEventListener('resize', resizeGrid);
    return () => window.removeEventListener('resize', resizeGrid);
  }, []);

  function onCellEditStart(params, event) {
    if (event.code != 'Enter') {
      const { id, field, row } = params;
      setTimeout(() => {
        apiRef?.current?.setEditCellValue({ id, field, value: row[field] });
      }, 10);
    }
  }

  return (
    gridProps && (
      <DataGridPremium
        {...gridProps}
        columns={newColumns}
        rows={newRows}
        apiRef={apiRef}
        initialState={{
          pinnedColumns: {
            left: [],
          },
        }}
        disableAggregation={true}
        cellSelection
        onClipboardCopy={() => handleClipboardCopy(apiRef)}
        sx={{
          maxHeight: '100%',
          ...(gridProps.isUndoing && {
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'transparent !important',
            },
          }),
          '& *, & *::before, & *::after': {
            boxSizing: 'content-box',
          },
          '& .MuiDataGrid-row': {
            transition: 'none !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto',
          },
          '& .newRow': {
            backgroundColor: '#7EC0A91A',
            borderTop: '1px solid #7EC0A9',
          },
          '& .newRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #08875B',
          },
          '& .editedRow': {
            backgroundColor: '#879CE91A',
            borderTop: '1px solid #879CE9',
          },

          '& .editedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #255FCC',
          },
          '& .deletedRow': {
            backgroundColor: '#FCC1BD1A',
            borderTop: '1px solid #FCC1BD',
          },

          '& .deletedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #E4002B',
          },
          '& .clonedRow': {
            backgroundColor: '#7EC0A91A',
            borderTop: '1px solid #7EC0A9',
          },
          '& .clonedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #08875B',
          },
          '& .errorRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            borderLeft: '3px solid #E4002B',
          },
          '& .MuiDataGrid-row.Mui-selected.newRow': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.Mui-selected.clonedRow': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.Mui-selected.editedRow': {
            backgroundColor: '#879CE91A !important',
          },
          '& .MuiDataGrid-row.Mui-selected.deletedRow': {
            backgroundColor: '#FCC1BD1A !important',
          },
          '& .MuiDataGrid-row.newRow:hover': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.editedRow:hover': {
            backgroundColor: '#879CE91A !important',
          },
          '& .MuiDataGrid-row.deletedRow:hover': {
            backgroundColor: '#FCC1BD1A !important',
          },
          '& .MuiDataGrid-row.clonedRow:hover': {
            backgroundColor: '#7EC0A91A !important',
          },
          '& .MuiDataGrid-row.newRow .MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-row.newRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#f3f9f7 !important',
            },
          '& .MuiDataGrid-row.editedRow .MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-row.editedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#f3f6fd !important',
            },
          '& .MuiDataGrid-row.deletedRow .MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-row.deletedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#fff9f9 !important',
            },
          '& .MuiDataGrid-row.clonedRow .MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-row.clonedRow .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: '#f3f9f7 !important',
            },
          '& .evenRow': { backgroundColor: '#f7f9fa' },
          '& .oddRow': { backgroundColor: '#fff' },
          '& .MuiDataGrid-row.newRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f9f7 !important',
          },

          '& .MuiDataGrid-row.editedRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f6fd !important',
          },

          '& .MuiDataGrid-row.deletedRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#fff9f9 !important',
          },

          '& .MuiDataGrid-row.clonedRow:hover .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f9f7 !important',
          },

          '& .MuiDataGrid-row.newRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f9f7 !important',
          },

          '& .MuiDataGrid-row.editedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f6fd !important',
          },

          '& .MuiDataGrid-row.deletedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#fff9f9 !important',
          },

          '& .MuiDataGrid-row.clonedRow .MuiDataGrid-cell[data-field="undoDelete"]': {
            backgroundColor: '#f3f9f7 !important',
          },

          '& .MuiDataGrid-cell[data-field="undoDelete"]': {
            paddingLeft: '0px !important',
            paddingRight: '0px !important',
            zIndex: 1300,
          },

          [`& .${gridClasses.cell}`]: {
            padding: '0px 10px',
            marginLeft: 0,
            marginRight: 0,
          },

          [`& .${gridClasses.columnHeader}`]: {
            padding: '0px 10px',
            marginLeft: 0,
            marginRight: 0,
          },

          '& .MuiDataGrid-columnHeader--withRightBorder .MuiDataGrid-columnSeparator': {
            opacity: 1,
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            padding: 0,
          },
          '& .MuiDataGrid-cellContent': {
            padding: 0,
          },

          '& .MuiDataGrid-withBorderColor': { border: '0px !important' },
          '& .MuiDataGrid-headerFilterRow': { borderBottom: '1px solid #000' },

          '@media (max-width: 768px)': {
            '& .MuiDataGrid-virtualScrollerContent': {
              marginRight: '110px',
            },
          },
        }}
      />
    )
  );
}

const useStyles = createUseStyles({
  gridContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  '@media (max-width: 600px)': {
    gridContainer: {
      height: '80dvh',
    },
  },
  gridBox: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto',
    '@media (max-width: 479px)': {
      '& .w-250, & .w250': {
        width: '100% !important',
      },
    },
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingLeft: 10,
    gap: 5,
  },
  errorSlot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'width 0.15s ease',
  },
  undoSlot: {
    display: 'flex',
    alignItems: 'center',
  },
});
